/**
 * How much of a creator's location their portfolio shows (EPD-12486).
 *
 * Shared by the API, which trims the location it serves for a published
 * portfolio so a hidden city never leaves the server, and the app, which
 * formats the editor preview as the creator changes the setting. The stored
 * profile address is never changed; this only decides what the portfolio shows.
 */

export const PORTFOLIO_LOCATION_DISPLAYS = [
  "city_state_country",
  "state_country",
  "country",
  "hidden",
] as const;

export type PortfolioLocationDisplay =
  (typeof PORTFOLIO_LOCATION_DISPLAYS)[number];

/** New and existing portfolios show state and country unless the creator picks otherwise. */
export const DEFAULT_PORTFOLIO_LOCATION_DISPLAY: PortfolioLocationDisplay =
  "state_country";

export const PORTFOLIO_LOCATION_DISPLAY_LABELS: Record<
  PortfolioLocationDisplay,
  string
> = {
  city_state_country: "City, state, country",
  state_country: "State, country",
  country: "Country only",
  hidden: "Hide location",
};

/** The parts of a profile's primary address a portfolio can show. */
export interface PortfolioLocationParts {
  city: string | null;
  state: string | null;
  country: string | null;
}

export function isPortfolioLocationDisplay(
  value: unknown,
): value is PortfolioLocationDisplay {
  return (
    typeof value === "string" &&
    PORTFOLIO_LOCATION_DISPLAYS.some((display) => display === value)
  );
}

/**
 * The location line a portfolio header shows, or null for none (the row and
 * its pin are then left out). Parts the setting excludes are dropped, as are
 * empty ones, so "State, country" on an address with no state shows just the
 * country.
 */
export function formatPortfolioLocation(
  parts: PortfolioLocationParts | null | undefined,
  display: PortfolioLocationDisplay,
): string | null {
  if (!parts || display === "hidden") return null;
  const shown = {
    city_state_country: [parts.city, parts.state, parts.country],
    state_country: [parts.state, parts.country],
    country: [parts.country],
  }[display];
  const text = shown
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(", ");
  return text || null;
}
